'use client';
import { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import { usePathname } from 'next/navigation';

function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export default function SmoothScroller({ children }: { children: React.ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);
  const rafRef = useRef<number | null>(null);
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  // Scroll to top on route change
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [pathname]);

  // Initialize Lenis — skip on touch/mobile devices for native scroll performance
  useEffect(() => {
    if (isTouchDevice()) return;

    const init = () => {
      const lenis = new Lenis({
        smoothWheel: true,
        lerp: 0.1,
        duration: 1.2,
      });
      lenisRef.current = lenis;
      if (typeof window !== 'undefined') {
        (window as unknown as Record<string, unknown>).__lenis = lenis;
      }

      const raf = (time: number) => {
        lenis.raf(time);
        rafRef.current = requestAnimationFrame(raf);
      };
      rafRef.current = requestAnimationFrame(raf);
    };

    const idleId = 'requestIdleCallback' in window
      ? window.requestIdleCallback(init, { timeout: 200 })
      : setTimeout(init, 50);

    return () => {
      if ('requestIdleCallback' in window) {
        window.cancelIdleCallback(idleId as number);
      } else {
        clearTimeout(idleId as ReturnType<typeof setTimeout>);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (typeof window !== 'undefined') {
        delete (window as unknown as Record<string, unknown>).__lenis;
      }
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, []);

  return <>{children}</>;
}
